import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle2, Award, Download, ShieldCheck, Clock, Video, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { refreshInternshipAlerts } from '@/lib/useInternshipAlerts';
import { openCertificate } from '@/lib/certificates';

export const PLATFORM_LABEL = {
  google_meet: 'Google Meet', microsoft_teams: 'Microsoft Teams', zoom: 'Zoom', other: 'Online meeting',
};

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const MARK_PILL = {
  present: 'bg-emerald-500 text-white', absent: 'bg-red-500 text-white',
  excused: 'bg-amber-400 text-white',
};
const MARK = { present: 'P', absent: 'A', excused: 'E' };

/** Everything the family can see and do about one confirmed placement. */
export function PlacementPanel({ placement: p, compact = false, autoConfirm = false, onChanged }) {
  const [confirming, setConfirming] = useState(
    autoConfirm && p.participation.status !== 'confirmed'
    && p.withdrawal?.status !== 'approved' && !p.completion.school_status);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const ji = p.joining_instructions;
  const awaiting = p.participation.status !== 'confirmed';
  const withdrawn = p.withdrawal?.status === 'approved';
  const closed = Boolean(p.completion.school_status);

  return (
    <div data-testid={`placement-panel-${p.id}`}>
      {awaiting && !withdrawn && !closed && (
        <div className="rounded-xl bg-[#FFF7F5] border border-[#FBD9D2] px-4 py-3 flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-sm text-[#4B5563]">
            Please confirm {p.child_name}'s participation so the school and {p.company_name} can plan the days.
          </p>
          <Button size="sm" onClick={() => setConfirming(true)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-xs font-semibold" data-testid={`confirm-participation-${p.child_id}`}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Confirm participation
          </Button>
        </div>
      )}

      {p.career_name && (
        <p className="text-sm text-[#6B7280] mb-3 flex items-center gap-1.5" data-testid={`placement-career-${p.child_id}`}>
          Career:{' '}
          {p.career_slug ? (
            <a href={`/careers/${p.career_slug}`} target="_blank" rel="noreferrer" className="font-semibold text-[#F97D6A] hover:underline inline-flex items-center gap-1">
              {p.career_name}<ExternalLink className="h-3 w-3" />
            </a>
          ) : <span className="font-semibold text-[#1F2937]">{p.career_name}</span>}
        </p>
      )}

      {ji && closed && !showDetails && (
        <button onClick={() => setShowDetails(true)} className="text-[11px] font-semibold text-[#6B7280] hover:text-[#1F2937] underline" data-testid={`show-placement-details-${p.child_id}`}>
          Show joining details
        </button>
      )}
      {ji && (!closed || showDetails) && (
        <div className="rounded-xl bg-white border border-[#E5E7EB] px-4 py-3" data-testid={`joining-instructions-${p.child_id}`}>
          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Joining instructions</p>
          <ul className="mt-1.5 text-sm text-[#4B5563] space-y-1">
            <li>Report on {fmt(ji.reporting_start_date)}{ji.reporting_end_date ? ` until ${fmt(ji.reporting_end_date)}` : ''}, {ji.daily_start_time} – {ji.daily_end_time} ({(ji.mode || '').replace('_', '-')})</li>
            {ji.reporting_location && <li>Where to report: {ji.reporting_location}</li>}
            {ji.workplace_address && <li>Address: {ji.workplace_address}</li>}
            {ji.supervisor_name && <li>Supervisor: {ji.supervisor_name}{ji.supervisor_contact ? ` · ${ji.supervisor_contact}` : ''}</li>}
            {ji.items_to_bring && <li>Bring: {ji.items_to_bring}</li>}
            {ji.dress_code && <li>Dress code: {ji.dress_code}</li>}
            {ji.agenda && <li>Agenda: {ji.agenda}</li>}
            {ji.additional_instructions && <li>{ji.additional_instructions}</li>}
            {ji.communication_link && <li><a href={ji.communication_link} target="_blank" rel="noreferrer" className="text-[#F97D6A] hover:underline">School communication group</a></li>}
          </ul>
          {(ji.meeting_link || (ji.day_links || []).length > 0) && (
            <div className="mt-3 rounded-xl bg-[#F3F7FF] border border-[#DCE7FB] px-3 py-2.5" data-testid={`online-joining-${p.child_id}`}>
              <p className="text-[11px] font-semibold text-[#2A4B8D] uppercase tracking-wider flex items-center gap-1.5">
                <Video className="h-3.5 w-3.5" />Online joining
                {ji.meeting_platform && <span className="font-medium normal-case tracking-normal text-[#5B7BBF]">· {PLATFORM_LABEL[ji.meeting_platform] || 'Meeting'}</span>}
              </p>
              {ji.meeting_link && (
                <a href={ji.meeting_link} target="_blank" rel="noreferrer"
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#2A4B8D] hover:underline break-all"
                  data-testid={`meeting-link-${p.child_id}`}>
                  Join the internship session<ExternalLink className="h-3 w-3" />
                </a>
              )}
              {(ji.day_links || []).length > 0 && (
                <ul className="mt-1.5 space-y-1" data-testid={`meeting-day-links-${p.child_id}`}>
                  {ji.day_links.map(d => (
                    <li key={d.day} className="text-sm text-[#4B5563]">
                      {fmt(d.day)} ·{' '}
                      <a href={d.link} target="_blank" rel="noreferrer" className="font-semibold text-[#2A4B8D] hover:underline" data-testid={`meeting-day-${p.child_id}-${d.day}`}>
                        Join link
                      </a>
                      {d.note ? ` — ${d.note}` : ''}
                    </li>
                  ))}
                </ul>
              )}
              {ji.meeting_passcode && <p className="text-[11px] text-[#4B5563] mt-1">Passcode: {ji.meeting_passcode}</p>}
              {ji.meeting_notes && <p className="text-[11px] text-[#4B5563] mt-1">{ji.meeting_notes}</p>}
              <p className="text-[10px] text-[#8AA0CC] mt-1.5">Shared only with confirmed families — please do not forward it.</p>
            </div>
          )}
        </div>
      )}
      {!ji && !awaiting && !closed && (
        <p className="text-sm text-[#6B7280] flex items-center gap-1.5" data-testid={`awaiting-instructions-${p.child_id}`}>
          <Clock className="h-3.5 w-3.5" />The school will share the reporting details before the internship starts.
        </p>
      )}

      {p.attendance.marked > 0 && (
        <div className="mt-3 flex items-center gap-1.5" data-testid={`attendance-${p.child_id}`}>
          <span className="text-[11px] text-[#6B7280]">Attendance</span>
          {p.attendance.days.map(d => (
            <span key={d.day} title={fmt(d.day)} className={`h-5 w-5 rounded text-[10px] font-bold flex items-center justify-center ${d.status ? MARK_PILL[d.status] : 'bg-[#F3F4F6] text-[#9CA3AF]'}`}>
              {d.status ? MARK[d.status] : '·'}
            </span>
          ))}
          <span className="text-[11px] text-[#4B5563] ml-1">{p.attendance.summary}</span>
        </div>
      )}

      {p.certificate && (
        <button
          onClick={() => openCertificate(p.certificate.url, `${p.child_name.replace(/\s+/g, '-')}-internship-certificate.pdf`)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F97D6A] hover:underline"
          data-testid={`certificate-${p.child_id}`}
        >
          <Award className="h-4 w-4" />Download completion certificate
          <Download className="h-3.5 w-3.5" />
        </button>
      )}

      {!compact && (
        <div className="mt-3 pt-3 border-t border-[#EFF0F2] flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] text-[#9CA3AF]">
            {p.participation.confirmed_at
              ? `Acknowledged ${fmt(p.participation.confirmed_at)} by ${p.participation.confirmed_by}`
              : 'Attendance on every scheduled day is required.'}
          </p>
          {p.withdrawal ? (
            <span className="text-[11px] text-[#6B7280]" data-testid={`withdrawal-status-${p.child_id}`}>
              Withdrawal {p.withdrawal.status}{p.withdrawal.decision_note ? ` · “${p.withdrawal.decision_note}”` : ''}
            </span>
          ) : closed ? null : (
            <button onClick={() => setWithdrawing(true)} className="text-[11px] text-[#9CA3AF] hover:text-[#F97D6A] underline" data-testid={`request-withdrawal-${p.child_id}`}>
              Request exceptional withdrawal
            </button>
          )}
        </div>
      )}

      {confirming && <ConfirmDialog placement={p} onClose={() => setConfirming(false)} onDone={() => { setConfirming(false); onChanged?.(); }} />}
      {withdrawing && <WithdrawDialog placement={p} onClose={() => setWithdrawing(false)} onDone={() => { setWithdrawing(false); onChanged?.(); }} />}
    </div>
  );
}

export function ConfirmDialog({ placement: p, onClose, onDone }) {
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/parent/internship-placements/${p.id}/confirm`, { acknowledged: true });
      toast.success('Thank you — your confirmation is recorded');
      refreshInternshipAlerts();
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record your confirmation'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="confirm-participation-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Confirm {p.child_name}'s internship</DialogTitle>
          <DialogDescription className="text-xs">{p.school_name} · {p.program_name}</DialogDescription>
        </DialogHeader>
        <ul className="text-sm text-[#4B5563] space-y-1 bg-[#FAFBFC] rounded-xl px-3 py-2.5" data-testid="confirm-details">
          <li><b>Student:</b> {p.child_name}, grade {p.child_grade}</li>
          <li><b>Company:</b> {p.company_name}</li>
          <li><b>Internship:</b> {p.title}</li>
          <li><b>Dates:</b> {fmt(p.start_date)} – {fmt(p.end_date)}{p.duration_days ? ` · ${p.duration_days} day${p.duration_days === 1 ? '' : 's'}` : ''}</li>
          {(p.workplace_address || p.format) && <li><b>Where:</b> {p.workplace_address || '—'} ({(p.format || '').replace('_', '-')})</li>}
        </ul>
        <div className="text-sm text-[#4B5563] space-y-2 leading-relaxed">
          <p><b>Attendance.</b> Your child is expected on every scheduled day. The company records attendance daily and the school needs it for completion.</p>
          <p><b>Professional conduct.</b> Your child will follow the host organisation's instructions, safety rules and timings, and behave as a representative of the school.</p>
          <p><b>Cancellation.</b> Once confirmed, a place can only be released through the school. If something exceptional happens, use Request exceptional withdrawal — the school reviews each case.</p>
        </div>
        <label className="flex items-start gap-2 bg-[#FEF0EC] rounded-xl px-3 py-2.5 cursor-pointer">
          <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5 accent-[#F97D6A]" data-testid="confirm-ack-checkbox" />
          <span className="text-sm text-[#4B5563] leading-relaxed">
            I confirm {p.child_name}'s participation and accept the attendance requirement, the conduct expectations
            and the school's cancellation policy.
          </span>
        </label>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Not now</Button>
          <Button onClick={submit} disabled={saving || !ack} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="confirm-participation-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}Confirm participation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WithdrawDialog({ placement: p, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/parent/internship-placements/${p.id}/withdrawal`, { reason: reason.trim() });
      toast.success('Your request is with the school');
      refreshInternshipAlerts();
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send your request'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="withdrawal-request-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Request withdrawal for {p.child_name}</DialogTitle>
          <DialogDescription className="text-xs">
            {p.title} · {p.company_name}. The school reviews every request and will write back with its decision.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs font-medium text-[#1F2937]">Reason *</Label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Tell the school what has changed" className="mt-1.5 rounded-xl border-gray-200 min-h-[72px] text-sm resize-none" data-testid="withdrawal-request-reason" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving || !reason.trim()} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="submit-withdrawal-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
